import React from "react";
import { Youtube, Sparkles, TrendingUp, Crosshair } from "lucide-react";
import { Button } from "./components/ui/button";
import { SiteFooter } from "./SiteFooter";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startLogin() {
  const redirectUrl = window.location.origin + "/";
  window.location.href =
    "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
}

export function Login() {
  return (
    <div
      className="min-h-screen bg-[#050505] text-zinc-100 noise relative overflow-hidden flex flex-col"
      data-testid="login-screen"
    >
      {/* Ambient red glow */}
      <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-red-600/20 blur-[140px] pointer-events-none" />
      <div className="absolute -bottom-40 -right-32 w-[520px] h-[520px] rounded-full bg-red-500/10 blur-[140px] pointer-events-none" />

      <div className="flex-1 flex items-center justify-center px-6">
        <div className="relative w-full max-w-5xl grid lg:grid-cols-2 gap-12 items-center">
        {/* Left — pitch */}
        <div className="space-y-7 animate-fade-up">
          <div className="inline-flex items-center gap-2.5 px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/30 text-red-300 text-[11px] uppercase tracking-[0.2em] font-semibold">
            <Sparkles className="w-3.5 h-3.5" />
            YouTube ROI Attribution
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.05]">
            See exactly which video
            <br />
            drove the{" "}
            <span className="text-red-500 drop-shadow-[0_0_24px_rgba(255,0,0,0.55)]">
              sale
            </span>
            .
          </h1>
          <p className="text-zinc-400 max-w-md text-[15px] leading-relaxed">
            One pixel. UTM auto-attribution. Live competitor hook teardowns.
            VidROI connects your YouTube channel to your revenue — per video,
            per day, per dollar.
          </p>

          <div className="grid sm:grid-cols-3 gap-3 pt-3">
            <FeatureChip icon={TrendingUp} label="Per-video ROI" />
            <FeatureChip icon={Crosshair} label="One-line pixel" />
            <FeatureChip icon={Sparkles} label="Hook AI" />
          </div>
        </div>

        {/* Right — login card */}
        <div className="relative animate-fade-up">
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-red-500/10 to-transparent blur-2xl" />
          <div className="relative rounded-3xl border border-white/10 bg-black/50 backdrop-blur-xl p-8 sm:p-10 shadow-[0_30px_120px_-30px_rgba(255,0,0,0.45)]">
            <div className="flex items-center gap-3 mb-7">
              <div className="w-11 h-11 rounded-2xl bg-red-500/10 border border-red-500/30 flex items-center justify-center shadow-[0_0_24px_rgba(255,0,0,0.3)]">
                <Youtube className="w-5 h-5 text-red-500" />
              </div>
              <div className="leading-tight">
                <div className="text-white font-semibold tracking-tight text-lg">
                  Vid
                  <span className="text-red-500 drop-shadow-[0_0_10px_rgba(255,0,0,0.5)]">
                    ROI
                  </span>
                </div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-medium">
                  Analytics
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-semibold text-white tracking-tight mb-1">
              Sign in to your workspace
            </h2>
            <p className="text-sm text-zinc-400 mb-7">
              Your channels, pixel, and revenue dashboard — protected and only
              yours.
            </p>

            <Button
              onClick={startLogin}
              data-testid="login-google-btn"
              className="w-full h-12 bg-white text-zinc-900 hover:bg-zinc-100 font-medium text-[15px] border border-white/10 shadow-[0_0_30px_-10px_rgba(255,255,255,0.6)]"
            >
              <GoogleMark />
              <span className="ml-2.5">Continue with Google</span>
            </Button>

            <div className="mt-6 text-[11px] uppercase tracking-[0.18em] text-zinc-600 text-center">
              Powered by Emergent · No password required
            </div>
          </div>
        </div>
      </div>
      </div>
      <SiteFooter variant="marketing" />
    </div>
  );
}

function FeatureChip({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border border-white/10 bg-white/[0.03]">
      <Icon className="w-4 h-4 text-red-400" />
      <span className="text-sm text-zinc-200">{label}</span>
    </div>
  );
}

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}
